import { useEffect } from 'react';
import { Users, Star, Settings2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useProfessorScope } from '@/contexts/ProfessorScopeContext';
import { useProfessorTeam } from '@/hooks/useProfessorTeam';
import { NavLink } from '@/components/NavLink';
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@pps/ui/components/ui/sidebar";

/**
 * Switcher de escopo "ver como" do modo professor (equivalente ao
 * NetworkScopeSwitcher, mas trocando PROFESSOR em vez de CLUBE): o dono
 * alterna entre "Todos" (visao unificada, agregado do pool inteiro — default)
 * e um professor especifico convidado (visao individual, mesmo recorte que
 * aquele professor ve via RLS). So renderiza quando ha >= 2 professores no
 * pool (club_teachers) — com 1 so (o proprio dono), nao ha o que alternar.
 */
export function ProfessorScopeSwitcher() {
  const { state } = useSidebar();
  const collapsed = state === 'collapsed';
  const { t } = useTranslation();
  const { club, staffRole } = useAuth();
  const { professorScope, setProfessorScope } = useProfessorScope();
  const { teachers, isLoading } = useProfessorTeam(club?.id, club?.owner_id);

  const activeTeachers = teachers.filter((tch) => tch.is_active);
  // Só o DONO do espaço tem a visão geral "de todos" pra alternar — um
  // professor convidado só enxerga o próprio recorte de qualquer forma (RLS
  // 20260702170000/171000), então o seletor não faz sentido pra ele e ficava
  // aparecendo indevidamente (nenhuma checagem de dono existia antes).
  const isOwner = staffRole === 'owner';

  // Defensivo: se o teacherId persistido sumiu do pool (removido/desativado),
  // reseta pra 'all' — mesmo padrao do filtro local de Agenda.tsx.
  useEffect(() => {
    if (
      professorScope.type === 'teacher' &&
      !isLoading &&
      !activeTeachers.some((tch) => tch.id === professorScope.teacherId)
    ) {
      setProfessorScope({ type: 'all' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, activeTeachers, professorScope]);

  if (isLoading || activeTeachers.length <= 1 || !isOwner) return null;

  const isAllActive = professorScope.type === 'all';
  const isTeacherActive = (teacherId: string): boolean =>
    professorScope.type === 'teacher' && professorScope.teacherId === teacherId;

  return (
    <SidebarGroup>
      <SidebarGroupLabel className="text-[10px] font-semibold tracking-widest uppercase text-[hsl(var(--sidebar-muted))] px-3 mb-1 flex items-center gap-1.5">
        <Users className="h-3 w-3" />
        {!collapsed && <span>{t('professorScope.label')}</span>}
      </SidebarGroupLabel>
      <SidebarGroupContent>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              isActive={isAllActive}
              onClick={() => setProfessorScope({ type: 'all' })}
              className={`rounded-lg transition-colors ${
                isAllActive
                  ? 'bg-primary text-primary-foreground font-medium'
                  : 'text-sidebar-foreground hover:bg-sidebar-accent'
              }`}
            >
              <Star className={`h-4 w-4 ${isAllActive ? 'fill-current' : ''}`} />
              {!collapsed && <span>{t('professorScope.all')}</span>}
            </SidebarMenuButton>
          </SidebarMenuItem>

          {activeTeachers.map((tch) => {
            const active = isTeacherActive(tch.id);
            return (
              <SidebarMenuItem key={tch.id}>
                <SidebarMenuButton
                  isActive={active}
                  onClick={() => setProfessorScope({ type: 'teacher', teacherId: tch.id })}
                  className={`rounded-lg transition-colors ${
                    active
                      ? 'bg-primary text-primary-foreground font-medium'
                      : 'text-sidebar-foreground hover:bg-sidebar-accent'
                  }`}
                >
                  <span
                    className={`h-2 w-2 rounded-full shrink-0 ${
                      active ? 'bg-primary-foreground' : 'border border-current opacity-60'
                    }`}
                    aria-hidden
                  />
                  {!collapsed && <span className="truncate">{tch.name}</span>}
                </SidebarMenuButton>
              </SidebarMenuItem>
            );
          })}

          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              className="rounded-lg text-[hsl(var(--sidebar-muted))] hover:bg-sidebar-accent hover:text-sidebar-foreground"
            >
              <NavLink to="/professor/equipe" activeClassName="">
                <Settings2 className="h-4 w-4" />
                {!collapsed && <span className="text-xs">{t('professorScope.manage')}</span>}
              </NavLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}
